import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Pill, AlertCircle, FileText, Syringe, TrendingUp, Menu, X, Users, QrCode as QrCodeIcon, Baby, Stethoscope, Download, ShieldAlert, Shield, Eye, LogOut, Share2, Camera, CreditCard } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDateToDDMMYYYY } from '../utils/dateFormatter';
import Sidebar from './Sidebar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { QRCodeCanvas } from 'qrcode.react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { useViewOnly } from '../contexts/ViewOnlyContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = ({ baby }) => {
  const navigate = useNavigate();
  const { viewOnlyMode, viewerInfo, enableViewOnlyMode, disableViewOnlyMode } = useViewOnly();
  const [stats, setStats] = useState({});
  const [showSidebar, setShowSidebar] = useState(false);
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const [accessId, setAccessId] = useState('');
  const [activeQrTab, setActiveQrTab] = useState('share');
  const [activeManageTab, setActiveManageTab] = useState('sync'); // 'sync' or 'guest'
  const [authorizedUsers, setAuthorizedUsers] = useState([]);
  const [showPdfMenu, setShowPdfMenu] = useState(false);
  const pdfMenuRef = useRef(null);
  const [profileImage, setProfileImage] = useState(null);
  const fileInputRef = useRef(null);

  // Helper function to normalize gender display
  const normalizeGender = (gender) => {
    if (!gender) return 'Unknown';
    const lowerGender = gender.toLowerCase();
    if (lowerGender === 'boy' || lowerGender === 'male' || lowerGender === 'm') return 'Male';
    if (lowerGender === 'girl' || lowerGender === 'female' || lowerGender === 'f') return 'Female';
    return gender;
  };
  
  // Check if this is a synced device (joined via Join & Sync)
  const isSyncedDevice = localStorage.getItem('isSyncedDevice') === 'true';

  // Format date to DD/MM/YYYY
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    // If it's a Date object, convert it
    if (dateString instanceof Date) {
      const day = String(dateString.getDate()).padStart(2, '0');
      const month = String(dateString.getMonth() + 1).padStart(2, '0');
      const year = dateString.getFullYear();
      return `${day}/${month}/${year}`;
    }
    
    // If it's a string and already in DD/MM/YYYY format, return as is
    if (typeof dateString === 'string' && dateString.includes('/')) {
      return dateString;
    }
    
    // Otherwise convert from ISO format string
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const handleLogoutViewOnly = () => {
    disableViewOnlyMode();
    sessionStorage.removeItem('viewOnlyBaby');
    // Reload to show the main baby from localStorage
    window.location.href = '/';
  };

  useEffect(() => {
    fetchDashboardStats();
    fetchAuthorizedUsers();
  }, [baby]);

  // Click outside to close PDF menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pdfMenuRef.current && !pdfMenuRef.current.contains(event.target)) {
        setShowPdfMenu(false);
      }
    };

    if (showPdfMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPdfMenu]);

  const fetchDashboardStats = async () => {
    try {
      const [conditions, medications, allergies, prescriptions, vaccinations, growthRecords, temperatureRecords, bloodPressureRecords, sessions, insurance] = await Promise.all([
        axios.get(`${API}/medical-conditions/${baby.id}`),
        axios.get(`${API}/medications/${baby.id}`),
        axios.get(`${API}/allergies/${baby.id}`),
        axios.get(`${API}/prescriptions/${baby.id}`),
        axios.get(`${API}/vaccinations/${baby.id}`),
        axios.get(`${API}/growth-records/${baby.id}`),
        axios.get(`${API}/temperature/${baby.id}`),
        axios.get(`${API}/blood-pressure/${baby.id}`),
        axios.get(`${API}/medical-sessions/${baby.id}`),
        axios.get(`${API}/insurance/${baby.id}`)
      ]);

      const sortedGrowthRecords = growthRecords.data.sort((a, b) => {
        // Use created_at if available for accurate sorting
        if (a.created_at && b.created_at) {
          return new Date(b.created_at) - new Date(a.created_at);
        }
        return new Date(b.date_recorded) - new Date(a.date_recorded);
      });
      
      const sortedTemperatureRecords = temperatureRecords.data.sort((a, b) => {
        // Use created_at if available for accurate sorting
        if (a.created_at && b.created_at) {
          return new Date(b.created_at) - new Date(a.created_at);
        }
        return new Date(`${b.date_recorded} ${b.time_recorded}`) - new Date(`${a.date_recorded} ${a.time_recorded}`);
      });

      const sortedBloodPressureRecords = bloodPressureRecords.data.sort((a, b) => {
        // Use created_at if available for accurate sorting
        if (a.created_at && b.created_at) {
          return new Date(b.created_at) - new Date(a.created_at);
        }
        return new Date(`${b.date_recorded} ${b.time_recorded}`) - new Date(`${a.date_recorded} ${a.time_recorded}`);
      });

      setStats({
        conditions: conditions.data.length,
        medications: medications.data.length,
        allergies: allergies.data.length,
        prescriptions: prescriptions.data.length,
        sessions: sessions.data.length,
        insurance: insurance.data.length,
        vaccinations: {
          // Only count scheduled vaccinations (those with age_group) for progress
          total: vaccinations.data.filter(v => v.age_group).length,
          given: vaccinations.data.filter(v => v.age_group && v.status === 'given').length,
          due: vaccinations.data.filter(v => v.age_group && v.status === 'due').length,
          overdue: vaccinations.data.filter(v => v.age_group && v.status === 'overdue').length,
          manual: vaccinations.data.filter(v => !v.age_group).length // Manually added vaccinations
        },
        growthRecords: growthRecords.data.length,
        latestGrowth: sortedGrowthRecords.length > 0 ? sortedGrowthRecords[0] : null,
        temperatureRecords: temperatureRecords.data.length,
        latestTemperature: sortedTemperatureRecords.length > 0 ? sortedTemperatureRecords[0] : null,
        bloodPressureRecords: bloodPressureRecords.data.length,
        latestBloodPressure: sortedBloodPressureRecords.length > 0 ? sortedBloodPressureRecords[0] : null
      });
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      toast.error('Failed to load dashboard statistics');
    }
  };

  const calculateAge = (birthDate) => {
    let birth;
    
    // Handle DD/MM/YYYY format
    if (birthDate.includes('/')) {
      const [day, month, year] = birthDate.split('/');
      birth = new Date(year, month - 1, day); // month is 0-indexed
    } else {
      // Handle ISO format (YYYY-MM-DD)
      birth = new Date(birthDate);
    }
    
    const today = new Date();
    
    let years = today.getFullYear() - birth.getFullYear();
    let months = today.getMonth() - birth.getMonth();
    let days = today.getDate() - birth.getDate();
    
    // Adjust for negative days
    if (days < 0) {
      months--;
      const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      days += prevMonth.getDate();
    }
    
    // Adjust for negative months
    if (months < 0) {
      years--;
      months += 12;
    }
    
    // Build age string
    const parts = [];
    if (years > 0) {
      parts.push(`${years} year${years > 1 ? 's' : ''}`);
    }
    if (months > 0) {
      parts.push(`${months} month${months > 1 ? 's' : ''}`);
    }
    if (days > 0 || parts.length === 0) {
      parts.push(`${days} day${days !== 1 ? 's' : ''}`);
    }
    
    return parts.join(' ');
  };

  // Helper function to parse DD/MM/YYYY date format
  const parseDDMMYYYY = (dateStr) => {
    if (!dateStr) return 'N/A';
    // If already in readable format, return as is
    if (dateStr.includes('/')) {
      return dateStr; // Already in DD/MM/YYYY format
    }
    return dateStr;
  };

  // Fetch authorized users who have access to this baby
  const fetchAuthorizedUsers = async () => {
    if (!baby?.id) return;
    
    try {
      // Add timestamp to prevent caching and force fresh data
      const response = await axios.get(`${API}/access/baby/${baby.id}?t=${Date.now()}`, {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      console.log('Fetched access data:', response.data);
      
      // Filter out inactive access on frontend as well (double check)
      const activeAccess = response.data.filter(access => access.is_active !== false);
      
      const accessData = activeAccess.map(access => ({
        id: access.id,
        name: access.dependent_name,
        accessId: access.access_code,
        grantedDate: formatDate(access.granted_date),
        accessType: access.access_type
      }));
      
      console.log('Active authorized users:', accessData);
      setAuthorizedUsers(accessData);
    } catch (error) {
      console.error('Error fetching authorized users:', error);
      setAuthorizedUsers([]); // Clear list on error
    }
  };

  // Separate users by access type
  const syncUsers = authorizedUsers.filter(user => user.accessType === 'full_access');
  const guestUsers = authorizedUsers.filter(user => user.accessType === 'View Only');

  // Grant access to a new dependent
  // Revoke access for a dependent
  const handleRevokeAccess = async (accessId, userName) => {
    console.log('handleRevokeAccess called with:', { accessId, userName });
    
    if (!window.confirm(`Revoke access for ${userName}?`)) {
      console.log('User canceled revoke');
      return;
    }

    console.log('User confirmed, calling API...');
    try {
      const url = `${API}/access/${accessId}`;
      console.log('DELETE request to:', url);
      
      const response = await axios.delete(url);
      console.log('API response:', response.data);
      
      toast.success(`Access revoked for ${userName}`);
      
      // Refresh authorized users list
      await fetchAuthorizedUsers();
    } catch (error) {
      console.error('Error revoking access:', error);
      console.error('Error details:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to revoke access');
    }
  };

  // Revoke all access
  const handleRevokeAllAccess = async () => {
    console.log('handleRevokeAllAccess called');
    console.log('Authorized users count:', authorizedUsers.length);
    console.log('Baby ID:', baby?.id);
    
    if (authorizedUsers.length === 0) {
      toast.error('No active access to revoke');
      return;
    }

    if (!window.confirm(`Revoke access for all ${authorizedUsers.length} user(s)? This cannot be undone.`)) {
      console.log('User canceled revoke all');
      return;
    }

    console.log('User confirmed, calling API...');
    try {
      const url = `${API}/access/baby/${baby.id}/revoke-all`;
      console.log('DELETE request to:', url);
      
      const response = await axios.delete(url);
      console.log('API response:', response.data);
      
      toast.success(response.data.message);
      
      // Refresh authorized users list
      await fetchAuthorizedUsers();
    } catch (error) {
      console.error('Error revoking all access:', error);
      console.error('Error details:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to revoke all access');
    }
  };

  // Handle dependent access by code
  const handleDependentAccess = async () => {
    if (!accessId.trim()) {
      toast.error('Please enter a Baby ID');
      return;
    }

    try {
      const response = await axios.post(`${API}/access/view`, {
        baby_id: accessId.trim()
      });

      const { baby: babyData } = response.data;
      
      // Store baby data in sessionStorage for view-only access
      sessionStorage.setItem('viewOnlyBaby', JSON.stringify(babyData));
      
      // Enable view-only mode as guest
      enableViewOnlyMode('Guest');
      
      toast.success(`Access granted! Loading ${babyData.name}'s dashboard...`);
      setShowAccessModal(false);
      
      // Reload the page to trigger App.js to load the view-only baby
      window.location.href = '/';
      
    } catch (error) {
      console.error('Error accessing baby data:', error);
      toast.error(error.response?.data?.detail || 'Invalid Baby ID');
    }
  };

  const generatePDF = async () => {
    try {
      toast.info('Generating PDF...');
      
      // Fetch all data
      const [birthRecordRes, conditionsRes, temperatureRes, growthRes, vaccinationsRes, allergiesRes, medicationsRes, sessionsRes] = await Promise.all([
        axios.get(`${API}/birth-record/${baby.id}`).catch(() => ({ data: null })),
        axios.get(`${API}/medical-conditions/${baby.id}`).catch(() => ({ data: [] })),
        axios.get(`${API}/temperature/${baby.id}`).catch(() => ({ data: [] })),
        axios.get(`${API}/growth-records/${baby.id}`).catch(() => ({ data: [] })),
        axios.get(`${API}/vaccinations/${baby.id}`).catch(() => ({ data: [] })),
        axios.get(`${API}/allergies/${baby.id}`).catch(() => ({ data: [] })),
        axios.get(`${API}/medications/${baby.id}`).catch(() => ({ data: [] })),
        axios.get(`${API}/medical-sessions/${baby.id}`).catch(() => ({ data: [] }))
      ]);

      const doc = new jsPDF();
      let yPosition = 20;

      // Logo and Title - Matching dashboard header style
      console.log('Drawing PDF logo with "My Health Record" title');
      
      // Load and add the app logo
      try {
        const logoImg = new Image();
        logoImg.crossOrigin = 'anonymous';
        await new Promise((resolve, reject) => {
          logoImg.onload = resolve;
          logoImg.onerror = reject;
          logoImg.src = '/app_logo.png';
        });
        
        // Create canvas to convert image
        const canvas = document.createElement('canvas');
        canvas.width = logoImg.width || 64;
        canvas.height = logoImg.height || 64;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(logoImg, 0, 0);
        const logoDataUrl = canvas.toDataURL('image/png');
        
        // Add logo to PDF
        const logoX = 75;
        const logoSize = 10;
        doc.addImage(logoDataUrl, 'PNG', logoX, yPosition, logoSize, logoSize);
        
        // Title next to logo (using bold font to match header - Space Grotesk style)
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(18);
        doc.setTextColor(31, 41, 55); // Dark gray for professional look
        doc.text('My Health Record', logoX + logoSize + 4, yPosition + 7, { align: 'left' });
      } catch (logoError) {
        console.log('Logo load failed, using text only:', logoError);
        // Fallback: Just show title without logo
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(18);
        doc.setTextColor(31, 41, 55);
        doc.text('My Health Record', 105, yPosition + 6, { align: 'center' });
      }
      
      // Reset to normal font for rest of document
      doc.setFont('helvetica', 'normal');
      yPosition += 15;

      // Baby Profile
      doc.setFontSize(16);
      doc.setTextColor(0, 0, 0);
      doc.text('Profile Information', 14, yPosition);
      yPosition += 5;

      autoTable(doc, {
        startY: yPosition,
        head: [['Field', 'Value']],
        body: [
          ['Name', baby.name],
          ['Date of Birth', baby.date_of_birth], // Use date as-is (DD/MM/YYYY format)
          ['Age', calculateAge(baby.date_of_birth)],
          ['Gender', normalizeGender(baby.gender)],
          ['Blood Type', baby.blood_type || 'Not recorded'],
          ['Current Height', (stats.latestGrowth?.height || baby.height) > 0 ? (stats.latestGrowth ? `${stats.latestGrowth.height} cm` : `${baby.height} cm`) : 'Not recorded'],
          ['Current Weight', (stats.latestGrowth?.weight || baby.weight) > 0 ? (stats.latestGrowth ? `${stats.latestGrowth.weight} kg` : `${baby.weight} kg`) : 'Not recorded'],
          ['ID', baby.short_id || baby.id.slice(0, 8).toUpperCase()]
        ],
        theme: 'striped',
        headStyles: { fillColor: [37, 99, 235] },
        margin: { left: 14, right: 14 }
      });

      yPosition = doc.lastAutoTable.finalY + 15;

      // Birth Record
      if (birthRecordRes.data) {
        const birthRecord = birthRecordRes.data;
        doc.setFontSize(16);
        doc.text('Birth Record', 14, yPosition);
        yPosition += 5;

        autoTable(doc, {
          startY: yPosition,
          head: [['Field', 'Value']],
          body: [
            ['Sex', birthRecord.sex],
            ['Mode of Delivery', birthRecord.mode_of_delivery],
            ['Birth Weight', `${birthRecord.birth_weight} kg`],
            ['Length', birthRecord.length ? `${birthRecord.length} cm` : 'Not recorded'],
            ['Head Circumference', birthRecord.head_circumference ? `${birthRecord.head_circumference} cm` : 'Not recorded'],
            ['Blood Group', birthRecord.blood_group || 'Not recorded'],
            ['Family History', birthRecord.family_history || 'None recorded'],
            ['Allergies', birthRecord.allergies || 'None recorded']
          ],
          theme: 'striped',
          headStyles: { fillColor: [37, 99, 235] },
          margin: { left: 14, right: 14 }
        });

        yPosition = doc.lastAutoTable.finalY + 15;
      }

      // Temperature Readings
      if (temperatureRes.data.length > 0) {
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }

        doc.setFontSize(16);
        doc.text('Body Temperature Records', 14, yPosition);
        yPosition += 5;

        const tempData = temperatureRes.data
          .sort((a, b) => new Date(b.date_recorded) - new Date(a.date_recorded))
          .slice(0, 10)
          .map(temp => [
            `${temp.temperature}°${temp.unit}`,
            temp.method,
            formatDate(temp.date_recorded),
            temp.time_recorded,
            temp.notes || 'N/A'
          ]);

        autoTable(doc, {
          startY: yPosition,
          head: [['Temperature', 'Method', 'Date', 'Time', 'Notes']],
          body: tempData,
          theme: 'striped',
          headStyles: { fillColor: [249, 115, 22] },
          margin: { left: 14, right: 14 }
        });

        yPosition = doc.lastAutoTable.finalY + 15;
      }

      // Growth Records
      if (growthRes.data.length > 0) {
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }

        doc.setFontSize(16);
        doc.text('Growth Tracker', 14, yPosition);
        yPosition += 5;

        const growthData = growthRes.data
          .sort((a, b) => new Date(b.date_recorded) - new Date(a.date_recorded))
          .slice(0, 10)
          .map(record => [
            formatDate(record.date_recorded),
            `${record.height} cm`,
            `${record.weight} kg`,
            `${record.head_circumference || 'N/A'} cm`,
            record.notes || 'N/A'
          ]);

        autoTable(doc, {
          startY: yPosition,
          head: [['Date', 'Height', 'Weight', 'Head Circ.', 'Notes']],
          body: growthData,
          theme: 'striped',
          headStyles: { fillColor: [236, 72, 153] },
          margin: { left: 14, right: 14 }
        });

        yPosition = doc.lastAutoTable.finalY + 15;
      }

      // Vaccinations
      if (vaccinationsRes.data.length > 0) {
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }

        doc.setFontSize(16);
        doc.text('Vaccinations', 14, yPosition);
        yPosition += 5;

        const vaccineData = vaccinationsRes.data.map(vaccine => [
          vaccine.vaccine_name,
          vaccine.age,
          vaccine.status.toUpperCase(),
          vaccine.date_given ? formatDate(vaccine.date_given) : 'Not given',
          vaccine.trade_name || 'N/A'
        ]);

        autoTable(doc, {
          startY: yPosition,
          head: [['Vaccine', 'Age', 'Status', 'Date Given', 'Trade Name']],
          body: vaccineData,
          theme: 'striped',
          headStyles: { fillColor: [139, 92, 246] },
          margin: { left: 14, right: 14 }
        });

        yPosition = doc.lastAutoTable.finalY + 15;
      }

      // Medical Sessions
      if (sessionsRes.data.length > 0) {
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }

        doc.setFontSize(16);
        doc.text('Medical Sessions', 14, yPosition);
        yPosition += 5;

        const sessionData = sessionsRes.data
          .sort((a, b) => new Date(b.session_date) - new Date(a.session_date))
          .slice(0, 5)
          .map(session => [
            formatDate(session.session_date),
            session.doctor_name || 'N/A',
            session.diagnosis,
            session.medication || 'None',
            session.temperature ? `${session.temperature}°C` : 'N/A'
          ]);

        autoTable(doc, {
          startY: yPosition,
          head: [['Date', 'Doctor', 'Diagnosis', 'Medication', 'Temp']],
          body: sessionData,
          theme: 'striped',
          headStyles: { fillColor: [6, 182, 212] },
          margin: { left: 14, right: 14 }
        });

        yPosition = doc.lastAutoTable.finalY + 15;
      }

      // Allergies
      if (allergiesRes.data.length > 0) {
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }

        doc.setFontSize(16);
        doc.text('Allergies', 14, yPosition);
        yPosition += 5;

        const allergyData = allergiesRes.data.map(allergy => [
          allergy.allergen,
          allergy.reaction,
          allergy.severity,
          formatDate(allergy.diagnosed_date)
        ]);

        autoTable(doc, {
          startY: yPosition,
          head: [['Allergen', 'Reaction', 'Severity', 'Diagnosed Date']],
          body: allergyData,
          theme: 'striped',
          headStyles: { fillColor: [59, 130, 246] },
          margin: { left: 14, right: 14 }
        });

        yPosition = doc.lastAutoTable.finalY + 15;
      }

      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(10);
        doc.setTextColor(128, 128, 128);
        doc.text(`Generated on ${formatDate(new Date())} | Page ${i} of ${pageCount}`, 105, 285, { align: 'center' });
      }

      return doc;

    } catch (error) {
      console.error('Error generating PDF:', error);
      console.error('Error details:', error.message, error.stack);
      throw error;
    }
  };

  const handleViewPDF = async () => {
    try {
      setShowPdfMenu(false);
      toast.info('Generating PDF...');
      const doc = await generatePDF();
      
      console.log('PDF document generated, creating blob...');
      
      // Open PDF in new tab using bloburi
      const pdfDataUri = doc.output('bloburi');
      console.log('PDF blob URI created:', pdfDataUri);
      
      // Open in new window
      const newWindow = window.open('', '_blank');
      if (newWindow) {
        newWindow.location.href = pdfDataUri;
        toast.success('PDF opened in new tab!');
      } else {
        toast.error('Please allow pop-ups to view PDF');
      }
      
    } catch (error) {
      console.error('Error viewing PDF:', error);
      toast.error('Failed to view PDF: ' + error.message);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      setShowPdfMenu(false);
      toast.info('Generating PDF...');
      const doc = await generatePDF();
      
      // Download PDF with baby name
      const fileName = `${baby.name} Health Record.pdf`;
      doc.save(fileName);
      
      toast.success('PDF downloaded successfully! Check your Downloads folder.');
    } catch (error) {
      toast.error('Failed to download PDF: ' + error.message);
    }
  };

  const handleSharePDF = async () => {
    try {
      setShowPdfMenu(false);
      toast.info('Generating PDF...');
      const doc = await generatePDF();
      
      // Create blob for sharing
      const pdfBlob = doc.output('blob');
      const fileName = `${baby.name.replace(/\s+/g, '_')}_Health_Record.pdf`;
      const file = new File([pdfBlob], fileName, { type: 'application/pdf' });

      // Check if Web Share API is supported
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: 'My Health Record',
          text: `Health record for ${baby.name}`,
          files: [file]
        });
        toast.success('PDF shared successfully!');
      } else {
        // Fallback: Download the file
        doc.save(fileName);
        toast.info('Sharing not supported on this device. PDF downloaded instead.');
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        toast.info('Share cancelled');
      } else {
        toast.error('Failed to share PDF: ' + error.message);
      }
    }
  };

  const generateHealthRecordHTML = async () => {
    // Fetch all data
    const [birthRecordRes, conditionsRes, temperatureRes, growthRes, vaccinationsRes, allergiesRes, medicationsRes, sessionsRes] = await Promise.all([
      axios.get(`${API}/birth-record/${baby.id}`).catch(() => ({ data: null })),
      axios.get(`${API}/medical-conditions/${baby.id}`).catch(() => ({ data: [] })),
      axios.get(`${API}/temperature/${baby.id}`).catch(() => ({ data: [] })),
      axios.get(`${API}/growth-records/${baby.id}`).catch(() => ({ data: [] })),
      axios.get(`${API}/vaccinations/${baby.id}`).catch(() => ({ data: [] })),
      axios.get(`${API}/allergies/${baby.id}`).catch(() => ({ data: [] })),
      axios.get(`${API}/medications/${baby.id}`).catch(() => ({ data: [] })),
      axios.get(`${API}/medical-sessions/${baby.id}`).catch(() => ({ data: [] }))
    ]);

    // Create HTML content with logo matching header style
    // Get the origin for absolute URL
    const origin = window.location.origin;
    const htmlContent = `
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&display=swap');
      </style>
      <div style="font-family: 'Space Grotesk', Arial, sans-serif; padding: 40px; background: white; width: 800px;">
        <div style="display: flex; align-items: center; justify-content: center; gap: 12px; margin-bottom: 30px;">
          <img src="${origin}/app_logo.png" alt="Logo" style="width: 40px; height: 40px; border-radius: 8px;" crossorigin="anonymous" />
          <h1 style="color: #1f2937; margin: 0; font-family: 'Space Grotesk', Arial, sans-serif; font-weight: 700; font-size: 28px;">My Health Record</h1>
        </div>
        
        <div style="margin-bottom: 30px;">
          <h2 style="color: #1f2937; border-bottom: 2px solid #2563eb; padding-bottom: 10px;">Profile Information</h2>
          <table style="width: 100%; border-collapse: collapse; margin-top: 15px;">
            <tr style="background: #f3f4f6;"><td style="padding: 10px; border: 1px solid #e5e7eb; font-weight: bold;">Name:</td><td style="padding: 10px; border: 1px solid #e5e7eb;">${baby.name}</td></tr>
            <tr><td style="padding: 10px; border: 1px solid #e5e7eb; font-weight: bold;">Date of Birth:</td><td style="padding: 10px; border: 1px solid #e5e7eb;">${baby.date_of_birth}</td></tr>
            <tr style="background: #f3f4f6;"><td style="padding: 10px; border: 1px solid #e5e7eb; font-weight: bold;">Age:</td><td style="padding: 10px; border: 1px solid #e5e7eb;">${calculateAge(baby.date_of_birth)}</td></tr>
            <tr><td style="padding: 10px; border: 1px solid #e5e7eb; font-weight: bold;">Gender:</td><td style="padding: 10px; border: 1px solid #e5e7eb;">${normalizeGender(baby.gender)}</td></tr>
            <tr style="background: #f3f4f6;"><td style="padding: 10px; border: 1px solid #e5e7eb; font-weight: bold;">Blood Type:</td><td style="padding: 10px; border: 1px solid #e5e7eb;">${baby.blood_type || 'Not recorded'}</td></tr>
            <tr><td style="padding: 10px; border: 1px solid #e5e7eb; font-weight: bold;">Height:</td><td style="padding: 10px; border: 1px solid #e5e7eb;">${(stats.latestGrowth?.height || baby.height) > 0 ? (stats.latestGrowth ? `${stats.latestGrowth.height} cm` : `${baby.height} cm`) : 'Not recorded'}</td></tr>
            <tr style="background: #f3f4f6;"><td style="padding: 10px; border: 1px solid #e5e7eb; font-weight: bold;">Weight:</td><td style="padding: 10px; border: 1px solid #e5e7eb;">${(stats.latestGrowth?.weight || baby.weight) > 0 ? (stats.latestGrowth ? `${stats.latestGrowth.weight} kg` : `${baby.weight} kg`) : 'Not recorded'}</td></tr>
          </table>
        </div>

        ${birthRecordRes.data ? `
        <div style="margin-bottom: 30px;">
          <h2 style="color: #1f2937; border-bottom: 2px solid #f59e0b; padding-bottom: 10px;">Birth Record</h2>
          <table style="width: 100%; border-collapse: collapse; margin-top: 15px;">
            <tr style="background: #fef3c7;"><td style="padding: 10px; border: 1px solid #fde68a; font-weight: bold;">Sex:</td><td style="padding: 10px; border: 1px solid #fde68a;">${birthRecordRes.data.sex}</td></tr>
            <tr><td style="padding: 10px; border: 1px solid #fde68a; font-weight: bold;">Delivery:</td><td style="padding: 10px; border: 1px solid #fde68a;">${birthRecordRes.data.mode_of_delivery}</td></tr>
            <tr style="background: #fef3c7;"><td style="padding: 10px; border: 1px solid #fde68a; font-weight: bold;">Birth Weight:</td><td style="padding: 10px; border: 1px solid #fde68a;">${birthRecordRes.data.birth_weight} kg</td></tr>
            <tr><td style="padding: 10px; border: 1px solid #fde68a; font-weight: bold;">Blood Group:</td><td style="padding: 10px; border: 1px solid #fde68a;">${birthRecordRes.data.blood_group || 'Not recorded'}</td></tr>
          </table>
        </div>
        ` : ''}

        ${temperatureRes.data.length > 0 ? `
        <div style="margin-bottom: 30px;">
          <h2 style="color: #1f2937; border-bottom: 2px solid #f97316; padding-bottom: 10px;">Latest Temperature Readings</h2>
          <table style="width: 100%; border-collapse: collapse; margin-top: 15px;">
            <tr style="background: #fed7aa;"><th style="padding: 10px; border: 1px solid #fdba74;">Temperature</th><th style="padding: 10px; border: 1px solid #fdba74;">Date</th><th style="padding: 10px; border: 1px solid #fdba74;">Method</th></tr>
            ${temperatureRes.data.sort((a, b) => new Date(b.date_recorded) - new Date(a.date_recorded)).slice(0, 5).map((temp, i) => `
              <tr style="${i % 2 === 0 ? 'background: #ffedd5;' : ''}"><td style="padding: 10px; border: 1px solid #fdba74;">${temp.temperature}°${temp.unit}</td><td style="padding: 10px; border: 1px solid #fdba74;">${formatDate(temp.date_recorded)}</td><td style="padding: 10px; border: 1px solid #fdba74;">${temp.method}</td></tr>
            `).join('')}
          </table>
        </div>
        ` : ''}

        ${growthRes.data.length > 0 ? `
        <div style="margin-bottom: 30px;">
          <h2 style="color: #1f2937; border-bottom: 2px solid #ec4899; padding-bottom: 10px;">Growth Records</h2>
          <table style="width: 100%; border-collapse: collapse; margin-top: 15px;">
            <tr style="background: #fce7f3;"><th style="padding: 10px; border: 1px solid #fbcfe8;">Date</th><th style="padding: 10px; border: 1px solid #fbcfe8;">Height</th><th style="padding: 10px; border: 1px solid #fbcfe8;">Weight</th></tr>
            ${growthRes.data.sort((a, b) => new Date(b.date_recorded) - new Date(a.date_recorded)).slice(0, 5).map((record, i) => `
              <tr style="${i % 2 === 0 ? 'background: #fce7f3;' : ''}"><td style="padding: 10px; border: 1px solid #fbcfe8;">${formatDate(record.date_recorded)}</td><td style="padding: 10px; border: 1px solid #fbcfe8;">${record.height} cm</td><td style="padding: 10px; border: 1px solid #fbcfe8;">${record.weight} kg</td></tr>
            `).join('')}
          </table>
        </div>
        ` : ''}

        <div style="margin-top: 40px; text-align: center; color: #6b7280; font-size: 12px;">
          <p>Generated on ${formatDate(new Date())}</p>
          <p>My Health Record - ${baby.name}</p>
        </div>
      </div>
    `;

    return htmlContent;
  };

  const handleViewAsJPG = async () => {
    try {
      setShowPdfMenu(false);
      toast.info('Generating JPG image...');

      // Create HTML content
      const htmlContent = await generateHealthRecordHTML();

      // Create temporary container
      const container = document.createElement('div');
      container.innerHTML = htmlContent;
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      document.body.appendChild(container);

      // Wait for fonts and images to load
      await document.fonts.ready;
      await new Promise(resolve => setTimeout(resolve, 500)); // Extra delay for font rendering

      // Convert to canvas
      const canvas = await html2canvas(container.firstChild, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true
      });

      // Remove temporary container
      document.body.removeChild(container);

      // Convert canvas to blob
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        toast.success('JPG opened in new tab!');
      }, 'image/jpeg', 0.95);

    } catch (error) {
      console.error('Error generating JPG:', error);
      toast.error('Failed to generate JPG: ' + error.message);
    }
  };

  const handleProfileImageUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImage(reader.result);
        localStorage.setItem(`baby_profile_${baby.id}`, reader.result);
        toast.success('Profile image updated!');
      };
      reader.readAsDataURL(file);
    } else {
      toast.error('Please select a valid image file');
    }
  };

  // Load profile image from localStorage on mount
  useEffect(() => {
    const savedImage = localStorage.getItem(`baby_profile_${baby.id}`);
    if (savedImage) {
      setProfileImage(savedImage);
    }
  }, [baby.id]);

  const handleDownloadAsJPG = async () => {
    try {
      setShowPdfMenu(false);
      toast.info('Generating JPG image...');

      // Create HTML content
      const htmlContent = await generateHealthRecordHTML();

      // Create temporary container
      const container = document.createElement('div');
      container.innerHTML = htmlContent;
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      document.body.appendChild(container);

      // Wait for fonts and images to load
      await document.fonts.ready;
      await new Promise(resolve => setTimeout(resolve, 500)); // Extra delay for font rendering

      // Convert to canvas
      const canvas = await html2canvas(container.firstChild, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true
      });

      // Remove temporary container
      document.body.removeChild(container);

      // Convert canvas to blob and download
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${baby.name.replace(/\s+/g, '_')}_Health_Record.jpg`;
        link.click();
        URL.revokeObjectURL(url);
        toast.success('JPG downloaded successfully!');
      }, 'image/jpeg', 0.95);

    } catch (error) {
      console.error('Error generating JPG:', error);
      toast.error('Failed to download JPG: ' + error.message);
    }
  };

  // Arrange cards in specific 3x2 grid layout
  const dashboardCards = [
    // 1st row: Medical condition and Allergies
    {
      id: 'medical-condition',
      title: 'Medical Condition',
      description: stats.conditions > 0 ? `${stats.conditions} condition${stats.conditions > 1 ? 's' : ''}` : 'No conditions recorded',
      temperatureInfo: (() => {
        let vitalsInfo = [];
        if (stats.latestTemperature) {
          vitalsInfo.push(`Body Temp: ${stats.latestTemperature.temperature}°${stats.latestTemperature.unit}`);
        }
        if (stats.latestBloodPressure) {
          vitalsInfo.push(`BP: ${stats.latestBloodPressure.systolic}/${stats.latestBloodPressure.diastolic}`);
        }
        return vitalsInfo.length > 0 ? vitalsInfo.join(' • ') : null;
      })(),
      latestTemperature: stats.latestTemperature,
      latestBloodPressure: stats.latestBloodPressure,
      icon: Heart,
      iconClass: 'medical-condition',
      path: '/medical-conditions'
    },
    {
      id: 'allergies',
      title: 'Allergies',
      description: stats.allergies > 0 ? `${stats.allergies} allergen${stats.allergies > 1 ? 's' : ''}` : 'No allergies recorded',
      icon: ShieldAlert,
      iconClass: 'allergies',
      path: '/allergies'
    },
    // 2nd row: Vaccinations and Growth tracker
    {
      id: 'vaccinations',
      title: 'Vaccinations',
      description: stats.vaccinations 
        ? `${stats.vaccinations.given}/${stats.vaccinations.total} completed${stats.vaccinations.manual > 0 ? `\n(${stats.vaccinations.manual} Additional Vaccination${stats.vaccinations.manual > 1 ? 's' : ''})` : ''}`
        : 'Loading...',
      icon: Syringe,
      iconClass: 'vaccinations',
      path: '/vaccinations'
    },
    {
      id: 'growth-tracker',
      title: 'Growth Tracker',
      description: stats.growthRecords > 0 ? `${stats.growthRecords} record${stats.growthRecords > 1 ? 's' : ''}` : 'No records',
      icon: TrendingUp,
      iconClass: 'growth-tracker',
      path: '/growth-tracker'
    },
    // 3rd row: Birth Record and Medical Sessions
    {
      id: 'birth-record',
      title: 'Birth Record',
      description: 'Birth details and history',
      icon: Baby,
      iconClass: 'birth-record',
      path: '/birth-record'
    },
    {
      id: 'medical-sessions',
      title: 'Medical Sessions',
      description: stats.sessions > 0 ? `${stats.sessions} session${stats.sessions > 1 ? 's' : ''}` : 'No sessions',
      icon: Stethoscope,
      iconClass: 'medical-sessions',
      path: '/medical-sessions'
    },
    // 4th row: Memories and Insurance
    {
      id: 'memories',
      title: 'Memories',
      description: 'Capture precious milestones',
      icon: Camera,
      iconClass: 'memories',
      path: '/memories'
    },
    {
      id: 'insurance',
      title: 'Insurance',
      description: stats.insurance > 0 ? `${stats.insurance} polic${stats.insurance > 1 ? 'ies' : 'y'}` : 'No policies',
      icon: CreditCard,
      iconClass: 'insurance',
      path: '/insurance'
    }
  ];

  // Safety check: ensure baby object is valid
  if (!baby || !baby.id || !baby.name) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Loading baby profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="dashboard-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button 
              className="p-2 rounded-lg bg-white bg-opacity-20 hover:bg-opacity-30 transition-colors"
              onClick={() => setShowSidebar(true)}
              data-testid="sidebar-menu-button"
            >
              <Menu className="w-5 h-5 text-white" />
            </button>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'}}>
              <Baby className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-xl sm:text-2xl font-bold" style={{fontFamily: 'Space Grotesk'}}>My Health Record</h1>
            {viewOnlyMode && (
              <div className="flex items-center gap-2 ml-4 px-3 py-1 bg-yellow-400 rounded-full">
                <Eye className="w-4 h-4 text-yellow-900" />
                <span className="text-xs font-semibold text-yellow-900">VIEW ONLY</span>
              </div>
            )}
          </div>
          <div className="flex items-center space-x-3">
            {viewOnlyMode ? (
              // In guest mode, don't show Close Viewing in header
              <></>
            ) : (
              <>
                {/* Hide Access Management for synced devices */}
                {!isSyncedDevice && (
                  <>
                    <button 
                      className="p-2 rounded-lg bg-white bg-opacity-20 hover:bg-opacity-30 transition-colors"
                      onClick={() => setShowAccessModal(true)}
                      data-testid="access-dependent-button"
                      title="Access as Dependent"
                    >
                      <Users className="w-5 h-5 text-white" />
                    </button>
                    <button 
                      className="p-2 rounded-lg bg-white bg-opacity-20 hover:bg-opacity-30 transition-colors"
                      onClick={() => setShowQrModal(true)}
                      data-testid="qr-code-button"
                      title="QR Code"
                    >
                      <QrCodeIcon className="w-5 h-5 text-white" />
                    </button>
                  </>
                )}
                <div className="relative" ref={pdfMenuRef}>
                  <button 
                    className="p-2 rounded-lg bg-white bg-opacity-20 hover:bg-opacity-30 transition-colors"
                    onClick={() => setShowPdfMenu(!showPdfMenu)}
                    data-testid="share-pdf-button"
                    title="Share Options"
                  >
                    <Share2 className="w-5 h-5 text-white" />
                  </button>
              
              {showPdfMenu && (
                <div className="absolute right-0 top-12 bg-white rounded-lg shadow-lg border border-gray-200 py-2 min-w-[200px] z-50">
                  <button
                    onClick={handleViewPDF}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    <span>View as PDF</span>
                  </button>
                  
                  <button
                    onClick={handleSharePDF}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                    </svg>
                    <span>Share PDF</span>
                  </button>
                </div>
              )}
            </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Profile Section */}
      <div className={`profile-section ${['male', 'boy', 'm'].includes(baby?.gender?.toLowerCase()) ? 'male-balloons' : 'female-balloons'}`} data-testid="profile-section">
        <div className="flex items-center">
          <div 
            className={`relative profile-avatar ${!viewOnlyMode ? 'group cursor-pointer' : ''}`} 
            onClick={() => !viewOnlyMode && fileInputRef.current?.click()}
          >
            {profileImage ? (
              <img src={profileImage} alt={baby.name} className="w-full h-full object-cover rounded-full" />
            ) : (
              <span>{baby?.name?.charAt(0)?.toUpperCase() || 'B'}</span>
            )}
            {!viewOnlyMode && (
              <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
            )}
          </div>
          {!viewOnlyMode && (
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleProfileImageUpload}
              className="hidden"
            />
          )}
          <div className="flex-1">
            <h2 className="text-xl font-bold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>
              {baby.name.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}
            </h2>
            <p className="text-sm text-gray-500">Date of Birth: {baby.date_of_birth}</p>
            <p className="text-gray-600">{calculateAge(baby.date_of_birth)} • {normalizeGender(baby.gender)}</p>
            {(() => {
              const height = stats.latestGrowth?.height || baby.height;
              const weight = stats.latestGrowth?.weight || baby.weight;
              if (height > 0 || weight > 0) {
                return (
                  <p className="text-sm text-gray-500">
                    {height > 0 ? `${height}cm` : ''}{height > 0 && weight > 0 ? ' • ' : ''}{weight > 0 ? `${weight}kg` : ''}
                  </p>
                );
              }
              return null;
            })()}
            {baby.blood_type && (
              <div className="mt-2 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                <span className="font-semibold">Blood Group: {baby.blood_type}</span>
              </div>
            )}
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-500 mb-1">Share this ID:</div>
            <div className="text-xl font-bold text-blue-600 bg-blue-50 px-4 py-2 rounded-lg border-2 border-blue-200">
              {baby.short_id || baby.id.slice(0, 8).toUpperCase()}
            </div>
            <div className="text-xs text-gray-400 mt-1">Baby ID</div>
          </div>
        </div>
      </div>

      {/* Parent Names Card */}
      {(baby.mother_name || baby.father_name) && (
        <div className="px-6 pb-3">
          <div className="bg-white rounded-lg shadow-md p-3">
            <div className="grid grid-cols-2 gap-4">
              {baby.mother_name && (
                <div className="flex items-center gap-2">
                  <p className="text-xs text-gray-500">Mother:</p>
                  <p className="text-base font-semibold text-gray-900">{baby.mother_name}</p>
                </div>
              )}
              {baby.father_name && (
                <div className="flex items-center gap-2">
                  <p className="text-xs text-gray-500">Father:</p>
                  <p className="text-base font-semibold text-gray-900">{baby.father_name}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Dashboard Cards Grid */}
      <div className="px-6 pb-6">
        <div className="dashboard-cards-grid-fixed">
          {dashboardCards
            .filter(card => {
              // Hide Memories tab for guest/view-only access
              if (viewOnlyMode && card.id === 'memories') {
                return false;
              }
              return true;
            })
            .map((card) => {
            const IconComponent = card.icon;
            return (
              <div
                key={card.id}
                className="dashboard-card"
                onClick={() => navigate(card.path)}
                data-testid={`${card.id}-card`}
              >
                <div className={`card-icon ${card.iconClass}`}>
                  <IconComponent size={24} />
                </div>
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2" style={{fontFamily: 'Space Grotesk'}}>
                    {card.title}
                  </h3>
                  <p className="text-sm text-gray-600">{card.description}</p>
                  {card.temperatureInfo && (
                    <div className="mt-2">
                      <p className="text-xs text-orange-600 font-medium">{card.temperatureInfo}</p>
                      <div className="flex justify-center gap-2 mt-1 flex-wrap">
                        {card.latestTemperature && card.latestTemperature.category && (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            card.latestTemperature.color === 'green' ? 'bg-green-100 text-green-800' :
                            card.latestTemperature.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {card.latestTemperature.category}
                          </span>
                        )}
                        {card.latestBloodPressure && card.latestBloodPressure.category && (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            card.latestBloodPressure.color === 'blue' ? 'bg-blue-100 text-blue-800' :
                            card.latestBloodPressure.color === 'green' ? 'bg-green-100 text-green-800' :
                            card.latestBloodPressure.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {card.latestBloodPressure.category}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

      </div>

      {/* Sidebar */}
      <Sidebar 
        isOpen={showSidebar} 
        onClose={() => setShowSidebar(false)} 
        baby={baby} 
      />

      {/* Access as Dependent Modal */}
      <Dialog open={showAccessModal} onOpenChange={setShowAccessModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Users className="w-5 h-5" />
              <span>Access as Dependent</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <p className="text-gray-600 text-center">
              Enter the Baby ID to view the baby's health records
            </p>
            
            <div className="space-y-3">
              <label className="text-sm font-medium text-gray-900">Baby ID</label>
              <Input
                placeholder="Enter Baby ID"
                value={accessId}
                onChange={(e) => setAccessId(e.target.value.toUpperCase())}
                className="uppercase"
              />
              <p className="text-xs text-gray-500">
                Enter the Baby ID provided by the baby's parent
              </p>
            </div>
            
            <div className="flex space-x-3">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => {
                  setShowAccessModal(false);
                  setAccessId('');
                }}
              >
                Cancel
              </Button>
              <Button 
                className="flex-1"
                onClick={handleDependentAccess}
                disabled={!accessId.trim()}
              >
                Access Dashboard
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* QR Code Modal */}
      <Dialog open={showQrModal} onOpenChange={setShowQrModal}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" style={{ pointerEvents: 'auto' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <QrCodeIcon className="w-5 h-5" />
              <span>Dashboard Access Management</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <p className="text-gray-600 text-sm">
              Manage your health dashboard sharing and access control
            </p>
            
            <div className="flex space-x-3 border-b pb-4">
              <button 
                className={`px-4 py-2 rounded-lg text-sm font-medium ${activeQrTab === 'share' ? 'bg-blue-500 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                onClick={() => setActiveQrTab('share')}
              >
                Share Access
              </button>
              <button 
                className={`px-4 py-2 rounded-lg text-sm font-medium ${activeQrTab === 'manage' ? 'bg-blue-500 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                onClick={() => setActiveQrTab('manage')}
              >
                Manage Access ({authorizedUsers.length})
              </button>
            </div>
            
            {activeQrTab === 'share' && (
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Access Level</h4>
                <div className="flex items-start space-x-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h5 className="font-medium text-blue-900">View Only Access</h5>
                    <p className="text-sm text-blue-700">People with access can only view your health data</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-center mb-4">
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <QRCodeCanvas 
                      value={JSON.stringify({
                        babyId: baby.id,
                        name: baby.name,
                        accessType: 'view-only',
                        url: `${window.location.origin}?guest=${baby.id}`
                      })}
                      size={180}
                      level="H"
                      includeMargin={true}
                    />
                  </div>
                </div>
                <p className="text-xs text-center text-gray-500">
                  Scan this QR code to access {baby.name}'s health dashboard
                </p>
              </div>
              
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-900">Share your unique ID (View Only):</label>
                  <div className="flex mt-1">
                    <Input 
                      value={baby.short_id || baby.id.slice(0, 8).toUpperCase()} 
                      readOnly 
                      className="flex-1"
                    />
                    <Button 
                      variant="outline" 
                      className="ml-2"
                      onClick={() => {
                        navigator.clipboard.writeText(baby.short_id || baby.id.slice(0, 8).toUpperCase());
                        toast.success('ID copied to clipboard!');
                      }}
                    >
                      Copy
                    </Button>
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-900">Share link:</label>
                  <div className="flex mt-1">
                    <Input 
                      value={`${window.location.origin}?guest=${baby.id}`} 
                      readOnly 
                      className="flex-1 text-sm"
                    />
                    <Button 
                      variant="outline" 
                      className="ml-2"
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}?guest=${baby.id}`);
                        toast.success('Link copied to clipboard!');
                      }}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                      </svg>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            )}
            
            {activeQrTab === 'manage' && (
            <div className="space-y-4" style={{ pointerEvents: 'auto' }}>
              {/* Sub-tabs for Join & Sync vs Guest */}
              <div className="flex border-b border-gray-200">
                <button
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    activeManageTab === 'sync'
                      ? 'border-b-2 border-blue-500 text-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => setActiveManageTab('sync')}
                >
                  Join & Sync ({syncUsers.length})
                </button>
                <button
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    activeManageTab === 'guest'
                      ? 'border-b-2 border-blue-500 text-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => setActiveManageTab('guest')}
                >
                  Guest ({guestUsers.length})
                </button>
              </div>

              {/* Join & Sync Tab Content */}
              {activeManageTab === 'sync' && (
              <div>
              <div className="flex items-center justify-between mb-3" style={{ pointerEvents: 'auto' }}>
                <h4 className="font-medium text-gray-900">Join & Sync Devices</h4>
                {syncUsers.length > 0 && (
                  <button
                    type="button"
                    className="px-3 py-1.5 text-sm font-medium text-red-600 bg-white border-2 border-red-300 rounded-md hover:bg-red-50 hover:text-red-700 transition-colors"
                    onClick={() => {
                      if (window.confirm(`Revoke access for all ${syncUsers.length} synced device(s)? This cannot be undone.`)) {
                        syncUsers.forEach(user => handleRevokeAccess(user.id, user.name));
                      }
                    }}
                  >
                    Revoke All
                  </button>
                )}
              </div>
              <div style={{ pointerEvents: 'auto' }}>
                <div className="space-y-3">
                  {syncUsers.length > 0 ? (
                    syncUsers.map((user) => (
                      <div key={user.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-blue-700 font-semibold text-sm">
                              {user.name.split(' ').map(n => n[0]).join('')}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{user.name}</p>
                            <p className="text-xs text-gray-500">ID: {user.accessId}</p>
                            <p className="text-xs text-gray-500">Access: {user.accessType} • Granted: {user.grantedDate}</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="px-3 py-1.5 text-sm font-medium text-red-600 bg-white border border-red-200 rounded-md hover:bg-red-50 hover:text-red-700 transition-colors"
                          onClick={() => handleRevokeAccess(user.id, user.name)}
                        >
                          Revoke
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <p>No synced devices yet</p>
                      <p className="text-sm mt-1">Use "Join & Sync" to add devices (max 2 apart from owner)</p>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-xs text-blue-800">
                  <strong>Note:</strong> Synced devices have full access (add/edit/delete). Limited to 2 devices apart from owner.
                </p>
              </div>
              </div>
              )}

              {/* Guest Tab Content */}
              {activeManageTab === 'guest' && (
              <div>
              <div className="flex items-center justify-between mb-3" style={{ pointerEvents: 'auto' }}>
                <h4 className="font-medium text-gray-900">Guest Access Devices</h4>
                {guestUsers.length > 0 && (
                  <button
                    type="button"
                    className="px-3 py-1.5 text-sm font-medium text-red-600 bg-white border-2 border-red-300 rounded-md hover:bg-red-50 hover:text-red-700 transition-colors"
                    onClick={() => {
                      if (window.confirm(`Revoke access for all ${guestUsers.length} guest device(s)? This cannot be undone.`)) {
                        guestUsers.forEach(user => handleRevokeAccess(user.id, user.name));
                      }
                    }}
                  >
                    Revoke All
                  </button>
                )}
              </div>
              <div style={{ pointerEvents: 'auto' }}>
                <div className="space-y-3">
                  {guestUsers.length > 0 ? (
                    guestUsers.map((user) => (
                      <div key={user.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                            <span className="text-green-700 font-semibold text-sm">
                              {user.name.split(' ').map(n => n[0]).join('')}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{user.name}</p>
                            <p className="text-xs text-gray-500">ID: {user.accessId}</p>
                            <p className="text-xs text-gray-500">Access: {user.accessType} • Granted: {user.grantedDate}</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="px-3 py-1.5 text-sm font-medium text-red-600 bg-white border border-red-200 rounded-md hover:bg-red-50 hover:text-red-700 transition-colors"
                          onClick={() => handleRevokeAccess(user.id, user.name)}
                        >
                          Revoke
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <p>No guest devices yet</p>
                      <p className="text-sm mt-1">Guest access is unlimited and view-only</p>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="bg-green-50 p-3 rounded-lg">
                <p className="text-xs text-green-800">
                  <strong>Note:</strong> Guest devices have view-only access. Unlimited devices allowed.
                </p>
              </div>
              </div>
              )}

            </div>
            )}
            
            <div className="pt-4">
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => {
                  setShowQrModal(false);
                  setActiveQrTab('share');
                }}
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;